/**
 * Document open/save — ADR-032 §3's third seam: *open a Document file; save
 * the current Document as a file.* The Document is the app's decision record
 * (ADR-006), and until now it could only be written by a probe and never read
 * back: the load door read GLB and FBX, and a session's decisions died with
 * the tab.
 *
 * OPEN GOES THROUGH THE ATOMIC DOOR. `openDocument` validates first and then
 * hands the Document to the SAME `swapTo` a dropped file walks — run first,
 * `store.load` only on success (Codex P2, #137). The store is never preloaded
 * with a Document the run may refuse; a refusing Document leaves the previous
 * subject on stage and the store still describing it.
 *
 * `upload:` SPECIMENS RELINK, LOUDLY (D6). A dropped file's specimen is
 * `upload:<filename>`, backed by a session-only map, so a saved Document names
 * bytes a later session does not hold. Embedding them is refused (a Document
 * is a decision record, not a container); loading half-linked is refused
 * harder. Instead the door WAITS: every missing `upload:` is listed by name on
 * the status line, and the next file of that name to arrive — by drop or by
 * the menu, the two gestures are one door — is held and the count goes down;
 * the Document runs the moment all are present. A remembered handle
 * (file-handles.ts) is tried first for each, so a browser that persists
 * handles relinks without asking. A file that is NOT one of the waited-for
 * names abandons the wait and says so — a new lineage is what the person asked
 * for by opening it. A waited-for file handed over under the WRONG menu entry
 * (the character under "Add animation…") is refused with the wait intact — the
 * entry's intent governs here as it does at the drop door (Codex P1, #140).
 * THE WAIT OUTLIVES A FAILED RUN: a file of the right name and the wrong bytes
 * throws out of the door, and the corrected file must still find the wait
 * (Codex P2, #140) — `pending` is cleared only once the run commits.
 *
 * THE SEQUENCE IS THE CALLER'S. Every entry takes the load sequence the door
 * reserved AT THE GESTURE, before any bytes were read — two files opened close
 * together must land in the order they were asked for, not the order their
 * reads finished (Codex P1, #140; the #94 stale-load guard depends on it).
 * Nothing here allocates a sequence of its own, and nothing here publishes a
 * wait for a load a newer gesture has already overtaken (`isCurrent`).
 *
 * ONLY THE LOADER PARSES. `parseDocument` begins a lineage (ADR-007 §4) and the
 * lint rule lets only main.ts import it, so the validator arrives here by
 * injection — this file is the loader's instrument, not a second loader, and
 * every decision it makes is testable with the real parser under test.
 *
 * Depends on: kernel types only. No DOM, no engine (session-no-engine).
 * Depended on by: main (the door); file-menu reaches it through main.
 */
import type { AnimatorDocument } from '@harryhausen/kernel';

export const UPLOAD_PREFIX = 'upload:';
/** One format, the one the validator already speaks (D5). */
export const DOCUMENT_EXTENSION = '.harry.json';

/** A `.json` file is read as a Document; everything else the door reads is a model or an animation. */
export function isDocumentFile(filename: string): boolean {
  return /\.json$/i.test(filename);
}

/** `upload:tiger.glb` → `tiger.glb`; any other uri is returned as it is. */
export function uploadName(uri: string): string {
  return uri.startsWith(UPLOAD_PREFIX) ? uri.slice(UPLOAD_PREFIX.length) : uri;
}

/**
 * Does a file's CONTENT read as a Document — JSON text opening with `{` (an
 * optional UTF-8 BOM and whitespace allowed)? The door routes on this after the
 * name, so a Document that lost its extension still opens instead of being
 * handed to a binary loader.
 */
export function looksLikeDocument(bytes: ArrayBuffer): boolean {
  const head = new Uint8Array(bytes, 0, Math.min(bytes.byteLength, 64));
  let i = head[0] === 0xef && head[1] === 0xbb && head[2] === 0xbf ? 3 : 0;
  while (
    i < head.length &&
    (head[i] === 0x20 || head[i] === 0x09 || head[i] === 0x0a || head[i] === 0x0d)
  ) {
    i += 1;
  }
  return head[i] === 0x7b;
}

export type RelinkKind = 'character' | 'animation';

export interface MissingUpload {
  readonly uri: string;
  readonly name: string;
  /** Which door satisfies it: the target is a character, a source an animation. */
  readonly kind: RelinkKind;
}

/** What the menu renders while a Document waits: its identity, its name, and the files it still needs. */
export interface PendingOpen {
  /** Opaque and unique per wait — a relink is bound to THIS, never to the Document's (editable, repeatable) name. */
  readonly id: string;
  readonly name: string;
  readonly missing: readonly MissingUpload[];
}

export type OpenOutcome =
  | { readonly kind: 'opened'; readonly doc: AnimatorDocument }
  | {
      readonly kind: 'waiting';
      readonly doc: AnimatorDocument;
      readonly missing: readonly MissingUpload[];
    }
  /** A later gesture overtook this load — it changed nothing, and what is on stage is speaking. */
  | { readonly kind: 'superseded' };

/**
 * What became of a file offered to a waiting Document: not wanted (no wait, or
 * a name it did not ask for — the wait is abandoned, loudly), held (one of
 * several, more to come), ran (the last one — the Document is on stage), or
 * superseded (a later gesture overtook this one; nothing changed).
 */
export type OfferOutcome = 'not-wanted' | 'held' | 'ran' | 'superseded';

export interface DocumentIoDeps {
  /** The validator — injected, because only the loader imports `parseDocument` (ADR-007 §4). */
  readonly parse: (raw: unknown) => AnimatorDocument;
  /** Can the session resolve this specimen uri right now — a bundled specimen, or bytes it holds? */
  readonly resolvable: (uri: string) => boolean;
  /** Hold a file's bytes under its `upload:` uri — the same map the drop door fills. */
  readonly hold: (uri: string, bytes: ArrayBuffer) => void;
  /** Forget bytes this door held — only if they are STILL the ones held; a newer load's under the same name stay. */
  readonly release: (uri: string, bytes: ArrayBuffer) => void;
  /** A cancel is a GESTURE: the in-flight run it abandons must not commit afterwards. */
  readonly invalidate: () => void;
  /** Try a remembered handle for a missing upload (D6). Null when none is kept or the browser refuses. */
  readonly recall: (uri: string) => Promise<File | null>;
  /** The one door. Reports whether it committed — a stale sequence returns false having changed nothing. */
  readonly swapTo: (doc: AnimatorDocument, seq: number) => Promise<boolean>;
  /** Is this load still the latest gesture? A wait is published only by a current load. */
  readonly isCurrent: (seq: number) => boolean;
  /** The status voice. `reveal` because a wait is a question put to a person, not a footnote. */
  readonly note: (line: string, reveal: boolean) => void;
}

export interface DocumentIo {
  /**
   * Read a Document file's text, validate it, relink what it names, run it
   * under the sequence the caller reserved at the gesture. THROWS the
   * validator's path-precise reasons for a file that is not a Document, and a
   * plain refusal for one naming a specimen no session could reach; both
   * belong on the status line via `refusalLine`.
   */
  openDocument(text: string, filename: string, seq: number): Promise<OpenOutcome>;
  /**
   * A file arrived through the door under `seq` — does a waiting Document
   * claim it? `intent` is the menu entry it came through, when it came through
   * one; a waited-for file under the wrong entry THROWS (wait intact).
   */
  offer(
    file: { readonly name: string },
    bytes: ArrayBuffer,
    seq: number,
    intent?: RelinkKind,
    /** The wait a RELINK was for (`PendingOpen.id`): if that wait has ended, the file is refused here, never imported. */
    relinkOf?: string,
  ): Promise<OfferOutcome>;
  pending(): PendingOpen | null;
  /** Drop the wait, saying why. A no-op when nothing waits. */
  cancelPending(reason: string): void;
  /** The Document as it stands, as JSON — no bytes embedded (D6). */
  serialize(doc: AnimatorDocument): string;
  documentFilename(doc: AnimatorDocument): string;
  /** The `upload:` uris a Document names — what a reopen will have to be shown again. */
  uploadsNamed(doc: AnimatorDocument): readonly string[];
}

const ENTRY_LABEL: Record<RelinkKind, string> = {
  character: 'Open character…',
  animation: 'Add animation…',
};

/** Bytes this door put into the shared map on a Document's behalf. */
interface Held {
  readonly uri: string;
  readonly bytes: ArrayBuffer;
}

export function createDocumentIo(deps: DocumentIoDeps): DocumentIo {
  let waitSeq = 0;
  /** A fresh identity per wait (Codex P1, #140 round 11): two Documents can share a name; two waits never share an id. */
  const nextWaitId = (): string => `wait-${++waitSeq}`;
  let pending: {
    readonly id: string;
    readonly doc: AnimatorDocument;
    readonly missing: readonly MissingUpload[];
    /**
     * Every buffer this door put in the shared map for THIS Document — recalled
     * from handles, or offered through the door. Released by identity when the
     * wait is cancelled, abandoned, or its run refuses (Codex P2, #140 rounds 4–5).
     */
    readonly staged: readonly Held[];
  } | null = null;

  const specimenUris = (doc: AnimatorDocument): readonly string[] => [
    ...new Set([doc.target.specimen.uri, ...doc.sources.map((s) => s.specimen.uri)]),
  ];
  const missingOf = (doc: AnimatorDocument, uri: string): MissingUpload => ({
    uri,
    name: uploadName(uri),
    kind: uri === doc.target.specimen.uri ? 'character' : 'animation',
  });
  const waitingLine = (doc: AnimatorDocument, missing: readonly MissingUpload[]): string => {
    const names = missing.map((m) => m.name);
    return (
      `"${doc.name}" waits for ${names.join(', ')} — drop ${names.length === 1 ? 'it' : 'each file'} here ` +
      `or relink through Open ▾; nothing on stage has changed`
    );
  };

  /**
   * Release what a wait staged, NEWEST FIRST (Codex P2, #140 round 8): a
   * name supplied twice is two buffers, and the door's release restores what
   * a buffer replaced — so the replacement must go before the original, or
   * the original would be put back and left resolvable.
   */
  const releaseAll = (staged: readonly Held[]): void => {
    for (const s of [...staged].reverse()) deps.release(s.uri, s.bytes);
  };

  return {
    async openDocument(text, filename, seq) {
      let raw: unknown;
      try {
        raw = JSON.parse(text);
      } catch (err) {
        throw new Error(
          `${filename} is not JSON — ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      const doc = deps.parse(raw);
      const uris = specimenUris(doc);
      // A `specimen:` uri names a bundled asset; one this build does not carry
      // will never arrive through the door, so waiting for it would be a lie.
      const foreign = uris.filter((u) => !u.startsWith(UPLOAD_PREFIX) && !deps.resolvable(u));
      if (foreign.length > 0) {
        throw new Error(
          `"${doc.name}" names ${foreign.length} specimen(s) this app cannot resolve: ${foreign.join(', ')} — ` +
            `only bundled specimens and files a person opened are reachable`,
        );
      }
      // A name the CURRENT wait staged is not "resolvable" for this open: those
      // bytes are unvalidated and about to be released (below, once this open
      // is known to be current), so a shared name is recalled fresh rather
      // than inherited (Codex P2, #140 round 6).
      const stagedByWait = new Set((pending?.staged ?? []).map((s) => s.uri));
      const missing: MissingUpload[] = [];
      // Recalled bytes are BUFFERED here and reach the shared map only once
      // this load is known to be current (Codex P1, #140 round 3): a superseded
      // open's recall could otherwise overwrite the current Document's bytes
      // under the same `upload:<name>`.
      const recalled: Held[] = [];
      for (const uri of uris) {
        if (deps.resolvable(uri) && !stagedByWait.has(uri)) continue;
        const file = await deps.recall(uri);
        if (file !== null) {
          recalled.push({ uri, bytes: await file.arrayBuffer() });
          continue;
        }
        missing.push(missingOf(doc, uri));
      }
      // The reads above took time; a newer gesture may have moved on. A load
      // that is no longer current publishes nothing — not a wait either, or an
      // older Document would present itself as the one the person is relinking
      // (Codex P1, #140). Held bytes it left behind are pruned by the next run.
      if (!deps.isCurrent(seq)) return { kind: 'superseded' };
      // Only NOW — current, and about to publish — does this open ABANDON any
      // earlier wait, the way a stranger drop does: its staged bytes are
      // released (nothing unvalidated stays resolvable) and the person hears
      // which wait ended. A stale open never gets here, so it cannot undo the
      // wait a newer gesture published (Codex P1, #140 round 7).
      if (pending !== null) {
        releaseAll(pending.staged);
        const name = pending.doc.name;
        pending = null;
        deps.note(`"${name}" abandoned — opening "${doc.name}" instead`, true);
      }
      for (const r of recalled) deps.hold(r.uri, r.bytes);
      if (missing.length > 0) {
        pending = { id: nextWaitId(), doc, missing, staged: recalled };
        deps.note(waitingLine(doc, missing), true);
        return { kind: 'waiting', doc, missing };
      }
      // Recalled bytes are held only as long as the run they were held FOR
      // (Codex P2, #140 round 4): a remembered file may be the corrupt one, and
      // bytes left in the map would make it "resolvable" — a reopen would skip
      // the handle and run against the poison for the rest of the session.
      let ran: boolean;
      try {
        ran = await deps.swapTo(doc, seq);
      } catch (err) {
        releaseAll(recalled);
        throw err;
      }
      if (!ran) releaseAll(recalled);
      return ran ? { kind: 'opened', doc } : { kind: 'superseded' };
    },

    async offer(file, bytes, seq, intent, relinkOf) {
      // A RELINK is for ONE wait, by identity (Codex P1, #140 rounds 10–11 —
      // not by the Document's name, which two Documents can share). The menu
      // checks at click time, but the wait can end during its awaits — a drop
      // commits, the person abandons — and then the file must be refused
      // HERE, atomically with the claim, rather than fall through to an
      // ordinary import that would begin a new lineage over the Document
      // that just opened. A different wait is not this one's to abandon.
      if (relinkOf !== undefined && (pending === null || pending.id !== relinkOf)) {
        throw new Error(`nothing to relink — "${file.name}" is no longer waited for`);
      }
      if (pending === null) return 'not-wanted';
      // The read between the gesture and this offer took time; a newer gesture
      // owns the stage now and this file claims nothing.
      if (!deps.isCurrent(seq)) return 'superseded';
      const uri = `${UPLOAD_PREFIX}${file.name}`;
      // A file the Document NAMES is always its own: missing, or already held
      // and supplied again (the corrected copy of one that refused). Only a
      // name the Document never asked for abandons the wait.
      const wanted =
        pending.missing.find((m) => m.uri === uri) ??
        (specimenUris(pending.doc).includes(uri) ? missingOf(pending.doc, uri) : undefined);
      if (wanted === undefined) {
        const name = pending.doc.name;
        // Its staged bytes go with it (Codex P2, #140 round 5): unvalidated
        // bytes left resolvable would let a later open skip the relink.
        releaseAll(pending.staged);
        pending = null;
        deps.note(
          `"${name}" abandoned — "${file.name}" is not a file it was waiting for; opening that instead`,
          true,
        );
        return 'not-wanted';
      }
      // The entry's intent governs (Codex P1, #140): the right file through the
      // wrong door is refused, and the wait — which the person did not
      // abandon — stays exactly as it was.
      if (intent !== undefined && intent !== wanted.kind) {
        throw new Error(
          `${file.name} is the ${wanted.kind} "${pending.doc.name}" waits for — relink it ` +
            `(Open ▾ › relink ${file.name}, or ${ENTRY_LABEL[wanted.kind]}), not ${ENTRY_LABEL[intent]}`,
        );
      }
      deps.hold(uri, bytes);
      const staged = [...pending.staged, { uri, bytes }];
      const rest = pending.missing.filter((m) => m.uri !== uri);
      if (rest.length > 0) {
        pending = { ...pending, missing: rest, staged };
        deps.note(waitingLine(pending.doc, rest), true);
        return 'held';
      }
      // All present. The wait STAYS until the run commits (Codex P2, #140): a
      // file of the right name and the wrong bytes throws out of the door, the
      // caller drops its bytes, and the person's corrected file must still find
      // this wait rather than import as a stranger.
      const doc = pending.doc;
      const before = { id: pending.id, doc, missing: pending.missing, staged };
      pending = before;
      let ran: boolean;
      try {
        ran = await deps.swapTo(doc, seq);
      } catch (err) {
        // EVERYTHING this door staged for the run goes (Codex P2, #140 rounds
        // 4–5): which file was bad is the loader's complaint to make, and a
        // poisoned buffer left in the map would be "resolvable" for the rest of
        // the session. The wait is restored only if it is still THIS wait —
        // abandoned or replaced while the run was in flight, it stays that way
        // — listing every upload the session no longer resolves.
        releaseAll(staged);
        if (pending === before) {
          pending = {
            id: before.id,
            doc,
            missing: specimenUris(doc)
              .filter((u) => u.startsWith(UPLOAD_PREFIX) && !deps.resolvable(u))
              .map((u) => missingOf(doc, u)),
            staged: [],
          };
        }
        throw err;
      }
      // Cleared only on a COMMIT (Codex P2, #140 round 3) — and only if it is
      // still this wait. A superseded run leaves the wait to whichever newer
      // gesture overtook it: a committed import abandons it, a committed open
      // replaces it, and a cancelled or refused one leaves it standing.
      if (ran && pending === before) pending = null;
      return ran ? 'ran' : 'superseded';
    },

    pending: () =>
      pending === null
        ? null
        : { id: pending.id, name: pending.doc.name, missing: pending.missing },

    cancelPending(reason) {
      if (pending === null) return;
      // The bytes staged for it go too (Codex P2, #140 round 5) — and the run
      // it may be abandoning mid-flight must not commit after the person was
      // told what is on stage stays (Codex P1, #140 round 8): a cancel is a
      // gesture, and the latest gesture wins.
      releaseAll(pending.staged);
      deps.invalidate();
      pending = null;
      deps.note(reason, true);
    },

    serialize: (doc) => `${JSON.stringify(doc, null, 2)}\n`,
    documentFilename: (doc) => `${doc.name.replace(/[^a-z0-9_-]/gi, '_')}${DOCUMENT_EXTENSION}`,
    uploadsNamed: (doc) => specimenUris(doc).filter((u) => u.startsWith(UPLOAD_PREFIX)),
  };
}
